import {CardHelp} from '../shared/components/CardHelp'
import {useCallback, useEffect, useMemo, useState} from 'react'
import api from 'axios'
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Database,
  Loader2,
  Package,
  RefreshCw,
  Save,
  Search,
  Truck,
} from 'lucide-react'

type LeadTimeSource = 'otif' | 'global' | 'manual'

interface Supplier {
  supplier_id: number
  fornecedor: string
  lead_time: number
  lead_time_source: LeadTimeSource
  product_count: number
  first_imported_at: string
  last_seen_at: string
  updated_at: string
}

interface SupplierApiResponse {
  success: boolean
  data?: Supplier[] | Supplier | null
  error?: string
}

const sourceLabels: Record<LeadTimeSource, {label: string; classes: string}> = {
  otif: {
    label: 'Calculado pelo OTIF',
    classes: 'border-blue-200 bg-blue-50 text-blue-700',
  },
  global: {
    label: 'Fallback inicial',
    classes: 'border-amber-200 bg-amber-50 text-amber-700',
  },
  manual: {
    label: 'Definido manualmente',
    classes: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  },
}

function formatDate(value: string): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('pt-BR')
}

export function SuppliersPage() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [drafts, setDrafts] = useState<Record<number, string>>({})
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const loadSuppliers = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const response = await api.get<SupplierApiResponse>(
        '/sellers/stock-intelligence/suppliers'
      )
      if (!response.data.success || !Array.isArray(response.data.data)) {
        throw new Error(response.data.error || 'Não foi possível carregar os fornecedores.')
      }
      const rows = response.data.data.map((supplier) => ({
        ...supplier,
        supplier_id: Number(supplier.supplier_id),
        lead_time: Number(supplier.lead_time),
        product_count: Number(supplier.product_count || 0),
      }))
      setSuppliers(rows)
      setDrafts(
        Object.fromEntries(rows.map((supplier) => [supplier.supplier_id, String(supplier.lead_time)]))
      )
    } catch (err: any) {
      setError(
        err?.response?.data?.error || err?.message || 'Não foi possível carregar os fornecedores.'
      )
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSuppliers()
  }, [loadSuppliers])

  const filteredSuppliers = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return suppliers
    return suppliers.filter((supplier) => supplier.fornecedor.toLowerCase().includes(term))
  }, [search, suppliers])

  const groups: Array<[string, Supplier[]]> = filteredSuppliers.length
    ? [['account', filteredSuppliers]]
    : []
  const productCount = useMemo(
    () => suppliers.reduce((total, supplier) => total + supplier.product_count, 0),
    [suppliers]
  )
  const manualCount = suppliers.filter((supplier) => supplier.lead_time_source === 'manual').length

  const saveLeadTime = async (supplier: Supplier) => {
    const value = Number(String(drafts[supplier.supplier_id] ?? '').replace(',', '.'))
    if (!Number.isFinite(value) || value < 0 || value > 365) {
      setError('Informe um lead time entre 0 e 365 dias.')
      return
    }

    setSavingId(supplier.supplier_id)
    setError('')
    setSuccess('')
    try {
      const response = await api.put<SupplierApiResponse>(
        `/sellers/stock-intelligence/suppliers/${supplier.supplier_id}/lead-time`,
        {lead_time: value}
      )
      if (!response.data.success || !response.data.data || Array.isArray(response.data.data)) {
        throw new Error(response.data.error || 'Não foi possível atualizar o lead time.')
      }
      const updated = response.data.data
      setSuppliers((current) =>
        current.map((item) =>
          item.supplier_id === supplier.supplier_id
            ? {
                ...updated,
                supplier_id: Number(updated.supplier_id),
                lead_time: Number(updated.lead_time),
                product_count: Number(updated.product_count || 0),
              }
            : item
        )
      )
      setDrafts((current) => ({
        ...current,
        [supplier.supplier_id]: String(Number(updated.lead_time)),
      }))
      setSuccess(`Lead time de ${supplier.fornecedor} atualizado.`)
    } catch (err: any) {
      setError(
        err?.response?.data?.error || err?.message || 'Não foi possível atualizar o lead time.'
      )
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between'>
        <div>
          <div className='mb-2 flex items-center gap-2 text-sm font-semibold text-blue-600'>
            <Truck className='h-4 w-4' />
            Rede de abastecimento
          </div>
          <h1 className='text-3xl font-bold text-gray-900'>Fornecedores</h1>
          <p className='mt-2 max-w-3xl text-sm text-gray-500'>
            Cada fornecedor possui um único lead time nesta conta, aplicado a todos os CNPJs. Depois do
            primeiro cadastro, novos imports preservam o valor; somente alterações manuais o modificam.
          </p>
        </div>
        <button
          type='button'
          onClick={loadSuppliers}
          disabled={isLoading}
          className='inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-50'
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      <div className='grid gap-4 sm:grid-cols-3'>
        <SummaryCard icon={Truck} label='Fornecedores' value={suppliers.length} color='blue' help='Total de fornecedores cadastrados ou identificados nos dados importados desta conta.' />
        <SummaryCard icon={Package} label='Produtos vinculados' value={productCount} color='violet' help='Quantidade de vínculos encontrados entre produtos e fornecedores.' />
        <SummaryCard icon={Save} label='Ajustes manuais' value={manualCount} color='emerald' help='Fornecedores cujo lead time automático foi substituído manualmente.' />
      </div>

      <div className='rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900'>
        <div className='flex items-start gap-3'>
          <Database className='mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600' />
          <div>
            <p className='font-semibold'>Regra de preservação ativa</p>
            <p className='mt-1 text-blue-800'>
              O import atualiza quando o fornecedor foi visto e quais produtos ele atende, mas nunca
              sobrescreve o lead time já cadastrado.
            </p>
          </div>
        </div>
      </div>

      {(error || success) && (
        <div
          className={`flex items-center gap-2 rounded-lg border p-4 text-sm ${
            error
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-emerald-200 bg-emerald-50 text-emerald-800'
          }`}
        >
          {error ? <AlertCircle className='h-5 w-5' /> : <CheckCircle2 className='h-5 w-5' />}
          {error || success}
        </div>
      )}

      <div className='relative max-w-xl'>
        <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder='Buscar fornecedor'
          className='w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
        />
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center rounded-xl border border-gray-200 bg-white py-24'>
          <Loader2 className='h-8 w-8 animate-spin text-blue-500' />
        </div>
      ) : groups.length === 0 ? (
        <div className='rounded-xl border border-dashed border-gray-300 bg-white py-20 text-center'>
          <Truck className='mx-auto h-10 w-10 text-gray-300' />
          <h2 className='mt-4 text-lg font-semibold text-gray-800'>Nenhum fornecedor encontrado</h2>
          <p className='mt-1 text-sm text-gray-500'>
            Os fornecedores aparecerão aqui após a importação do pacote Estoque, Entradas e OTIF.
          </p>
        </div>
      ) : (
        <div className='space-y-5'>
          {groups.map(([scope, accountSuppliers]) => (
            <section key={scope} className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='flex items-center justify-between border-b border-gray-200 bg-gray-50 px-5 py-4'>
                <div className='flex items-center gap-3'>
                  <div className='rounded-lg bg-white p-2 shadow-sm ring-1 ring-gray-200'>
                    <Package className='h-5 w-5 text-gray-600' />
                  </div>
                  <div>
                    <h2 className='font-semibold text-gray-900'>Todos os fornecedores da conta</h2>
                    <p className='text-xs text-gray-500'>
                      {accountSuppliers.length} {accountSuppliers.length === 1 ? 'fornecedor' : 'fornecedores'}
                    </p>
                  </div>
                </div>
              </div>

              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-white'>
                    <tr className='text-left text-xs font-semibold uppercase tracking-wide text-gray-500'>
                      <th className='px-5 py-3'>Fornecedor</th>
                      <th className='px-5 py-3'>Produtos</th>
                      <th className='px-5 py-3'>Origem</th>
                      <th className='px-5 py-3'>Último import</th>
                      <th className='px-5 py-3'>Lead time</th>
                      <th className='px-5 py-3 text-right'>Ação</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100'>
                    {accountSuppliers.map((supplier) => {
                      const source = sourceLabels[supplier.lead_time_source] ?? sourceLabels.otif
                      const draft = drafts[supplier.supplier_id] ?? ''
                      const hasChanged = Number(String(draft).replace(',', '.')) !== supplier.lead_time
                      return (
                        <tr key={supplier.supplier_id} className='hover:bg-gray-50/70'>
                          <td className='px-5 py-4'>
                            <div className='flex items-center gap-3'>
                              <div className='rounded-lg bg-blue-50 p-2 text-blue-600'>
                                <Truck className='h-4 w-4' />
                              </div>
                              <span className='font-medium text-gray-900'>{supplier.fornecedor}</span>
                            </div>
                          </td>
                          <td className='px-5 py-4 text-sm text-gray-600'>
                            <span className='inline-flex items-center gap-1.5'>
                              <Package className='h-4 w-4 text-gray-400' />
                              {supplier.product_count}
                            </span>
                          </td>
                          <td className='px-5 py-4'>
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${source.classes}`}>
                              {source.label}
                            </span>
                          </td>
                          <td className='px-5 py-4 text-sm text-gray-600'>
                            <span className='inline-flex items-center gap-1.5'>
                              <Clock3 className='h-4 w-4 text-gray-400' />
                              {formatDate(supplier.last_seen_at)}
                            </span>
                          </td>
                          <td className='px-5 py-4'>
                            <div className='flex items-center gap-2'>
                              <input
                                type='number'
                                min='0'
                                max='365'
                                step='0.1'
                                value={draft}
                                onChange={(event) =>
                                  setDrafts((current) => ({
                                    ...current,
                                    [supplier.supplier_id]: event.target.value,
                                  }))
                                }
                                disabled={savingId === supplier.supplier_id}
                                className='w-24 rounded-lg border border-gray-300 px-3 py-2 text-right text-sm font-semibold tabular-nums outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100'
                              />
                              <span className='text-sm text-gray-500'>dias</span>
                            </div>
                          </td>
                          <td className='px-5 py-4 text-right'>
                            <button
                              type='button'
                              onClick={() => saveLeadTime(supplier)}
                              disabled={!hasChanged || savingId === supplier.supplier_id}
                              className='inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400'
                            >
                              {savingId === supplier.supplier_id ? (
                                <Loader2 className='h-4 w-4 animate-spin' />
                              ) : (
                                <Save className='h-4 w-4' />
                              )}
                              Salvar
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  color,
  help,
}: {
  icon: typeof Truck
  label: string
  value: number
  color: 'blue' | 'violet' | 'emerald'
  help: string
}) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    violet: 'bg-violet-50 text-violet-600',
    emerald: 'bg-emerald-50 text-emerald-600',
  }
  return (
    <div className='relative rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
      <CardHelp content={help} className='absolute right-2 top-2' />
      <div className='flex items-center justify-between pr-7'>
        <div>
          <p className='text-sm font-medium text-gray-500'>{label}</p>
          <p className='mt-1 text-3xl font-bold tabular-nums text-gray-900'>{value}</p>
        </div>
        <div className={`rounded-xl p-3 ${colors[color]}`}>
          <Icon className='h-6 w-6' />
        </div>
      </div>
    </div>
  )
}
